/** Who a principal is to identity-service, as far as its token says. */
export type PrincipalKind = "human" | "agent" | "platform_service";

/**
 * The identity-service claims a v2 token carries, read from the ID token profile. Every field is
 * optional because v1 and break-glass tokens carry none of them and a person holding no membership
 * receives a v2 token without tenant fields; the role arrays are always present, empty when absent.
 */
export type IdentityClaims = {
  principalId?: string;
  principalKind?: PrincipalKind;
  tenantId?: string;
  tenantSlug?: string;
  platformRoles: string[];
  tenantRoles: string[];
  /** The upstream identity provider's user id, the join key to the registry's user records. */
  upstreamUserId?: string;
  /** The upstream identity provider's organization for the token's tenant (Zitadel's organization id). */
  upstreamTenantId?: string;
  /** Zitadel's resource-owner organization; present on v1 and Zitadel-direct tokens only. */
  zitadelOrganizationId?: string;
};

const PRINCIPAL_KINDS: readonly PrincipalKind[] = ["human", "agent", "platform_service"];

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/** Reads the identity-service claims out of an ID token profile; unknown or malformed values are dropped. */
export function parseIdentityClaims(profile: Record<string, unknown> | undefined): IdentityClaims {
  const p = profile ?? {};
  const kind = optionalString(p.principal_kind);
  const claims: IdentityClaims = {
    platformRoles: stringArray(p.platform_roles),
    tenantRoles: stringArray(p.tenant_roles),
  };
  const principalId = optionalString(p.principal_id);
  if (principalId) claims.principalId = principalId;
  if (kind && (PRINCIPAL_KINDS as readonly string[]).includes(kind)) claims.principalKind = kind as PrincipalKind;
  const tenantId = optionalString(p.tenant_id);
  if (tenantId) claims.tenantId = tenantId;
  const tenantSlug = optionalString(p.tenant_slug);
  if (tenantSlug) claims.tenantSlug = tenantSlug;
  const upstreamUserId = optionalString(p.upstream_user_id);
  if (upstreamUserId) claims.upstreamUserId = upstreamUserId;
  const upstreamTenantId = optionalString(p.upstream_tenant_id);
  if (upstreamTenantId) claims.upstreamTenantId = upstreamTenantId;
  const organizationId = optionalString(p["urn:zitadel:iam:user:resourceowner:id"]);
  if (organizationId) claims.zitadelOrganizationId = organizationId;
  return claims;
}
